import type { ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";

import { EmptyState } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { ErrorBoundaryWithLogging } from "app/pages/sales-intelligence/common-components/ErrorBoundaryWithLogging";
import {
    StyledLeadsBaseContentContainer,
    StyledLeadsTabContent,
    StyledLeadsTableWrapper,
} from "app/pages/sales-intelligence/common-components/layout";
import type { TabContentComponentProps } from "app/pages/sales-intelligence/common-components/tabbed-page-content";
import type { PageTabBase } from "app/pages/sales-intelligence/common-components/tabbed-page-content/types";
import { useIsLeadsPanelDataLoading } from "app/pages/sales-intelligence/modules/leads-search/hooks/useIsLeadsPanelDataLoading";
import {
    LeadsSearchFiltersWithAgent,
    selectCountriesFilterValue,
} from "app/pages/sales-intelligence/modules/leads-search-filters";
import { useResetTerritory } from "app/pages/sales-intelligence/modules/teams/hooks/useResetTerritory";
import { createContactsResultsTableColumns } from "app/pages/sales-intelligence/sub-modules/contacts-search/configuration/columns/createContactsResultsTableColumns";
import ContactsSearchResultsContainer from "app/pages/sales-intelligence/sub-modules/contacts-search/containers/ContactSearchResultContainer";
import { ContactsFlowProvider } from "app/pages/sales-intelligence/sub-modules/contacts-search/context/ContactsFlowContext";
import { useContactSearchResult } from "app/pages/sales-intelligence/sub-modules/contacts-search/hooks/useContactSearchResult";
import { exportSelectedContactsToExcelForLeadsSearchThunk } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/effects";
import type { ContactsSearchResults } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/common";
import type { ContactIdCompanyPair } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/dto";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";

import { TerritoryFiltersControlGate } from "../../components/TerritoryFiltersControlGate/TerritoryFiltersControlGate";
import { useAdvancedSearchContactsFlowStrategy } from "../../hooks/useAdvancedSearchContactsFlowStrategy";
import type { ContactsTabId } from "../../types/page-tabs";
import ContactsTableActionsPanelContainer from "../table-actions-panel/ContactsTableActionsPanel";

type Props = TabContentComponentProps<PageTabBase<ContactsTabId>>;

const INIT_CONTACTS_SEARCH_RESULTS: ContactsSearchResults = {
    totalCount: 0,
    rows: [],
};

const ContactsTabContentContainer = ({ shouldRenderDeferredContent = true }: Props): ReactElement => {
    const dispatch = useDispatch();
    const contactsFlowStrategy = useAdvancedSearchContactsFlowStrategy();
    const { data, isFetching, isPending, isError, error, refetch } = useContactSearchResult();

    const countriesFilterValue = useSelector(selectCountriesFilterValue);
    const countries = countriesFilterValue ? countriesFilterValue : [WORLDWIDE_COUNTRY_ID];

    const isLeadsPanelDataLoading = useIsLeadsPanelDataLoading();
    const resetTerritory = useResetTerritory();

    const isContentLoading = isFetching || isPending || !shouldRenderDeferredContent;

    async function handleExcelExport(contactIdCompanyPair: ContactIdCompanyPair): Promise<void> {
        await dispatch(
            exportSelectedContactsToExcelForLeadsSearchThunk(
                [contactIdCompanyPair],
                contactsFlowStrategy.dataSource.exportExcel,
            ),
        );
    }

    return (
        <ContactsFlowProvider value={contactsFlowStrategy}>
            <StyledLeadsTabContent data-automation="tab-content">
                <StyledLeadsBaseContentContainer>
                    <LeadsSearchFiltersWithAgent
                        isLoading={isLeadsPanelDataLoading || !shouldRenderDeferredContent}
                        belowSearchSlot={<TerritoryFiltersControlGate />}
                        onClearAllFilters={resetTerritory}
                    />
                </StyledLeadsBaseContentContainer>

                <StyledLeadsTableWrapper>
                    <ContactsSearchResultsContainer
                        isLoading={isContentLoading}
                        actionsPanel={<ContactsTableActionsPanelContainer />}
                        crmCountries={countries}
                        searchResults={data || INIT_CONTACTS_SEARCH_RESULTS}
                        isError={isContentLoading ? false : isError}
                        error={error}
                        onExportItemToExel={handleExcelExport}
                        onRefetchData={() => refetch()}
                        createColumnsConfigFactory={createContactsResultsTableColumns}
                    />
                </StyledLeadsTableWrapper>
            </StyledLeadsTabContent>
        </ContactsFlowProvider>
    );
};

// TODO the error boundary should be moved to the contacts table level (upon redesign)
const ContactsTabContentWithErrorBoundary = (props: Props) => (
    <ErrorBoundaryWithLogging
        fallback={
            <EmptyState
                title={translate("error.boundary.title")}
                iconName="no-data-tree"
            />
        }
    >
        <ContactsTabContentContainer {...props} />
    </ErrorBoundaryWithLogging>
);

export default ContactsTabContentWithErrorBoundary;
